// client/src/pages/Press.jsx
import React, { useState, useEffect } from 'react';
import { ExternalLink, Award, Calendar, Book, User, Download } from 'lucide-react';
import SEOLayout from '../components/layout/SEOLayout';

const Press = () => {
    const [pressData, setPressData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPressData = async () => {
            try {
                const response = await fetch('/api/content/press');
                const data = await response.json();
                setPressData(data);
            } catch (error) {
                console.error('Error loading press data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPressData();
    }, []);

    if (loading) {
        return (
            <SEOLayout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
                </div>
            </SEOLayout>
        );
    }

    const getSignificanceColor = (significance) => {
        switch (significance) {
            case 'high': return 'bg-yellow-100 text-yellow-800';
            case 'milestone': return 'bg-purple-100 text-purple-800';
            default: return 'bg-orange-100 text-orange-800';
        }
    };

    return (
        <SEOLayout
            title="Press & Recognition"
            description={pressData?.hero?.description}
            keywords="Louis Silverstein press, awards, recognition, VFX achievements, technology innovation"
        >
            {/* Hero Section */}
            <section className="bg-orange-700 py-16" aria-labelledby="press-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 id="press-heading" className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl">
                            {pressData?.hero?.title}
                        </h1>
                        <p className="mt-6 max-w-2xl mx-auto text-xl text-orange-100">
                            {pressData?.hero?.description}
                        </p>
                    </div>
                </div>
            </section>

            {/* Featured Recognition */}
            <section className="py-16 bg-white" aria-labelledby="featured-recognition-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:text-center mb-12">
                        <h2 id="featured-recognition-heading" className="text-base text-orange-600 font-semibold tracking-wide uppercase">
                            Major Recognition
                        </h2>
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                            Awards & Achievements
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-1">
                        {pressData?.featured_recognition?.map((item) => (
                            <article key={item.id} className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                                <div className="p-8">
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-3">
                                                <Award className="h-8 w-8 text-yellow-600" aria-hidden="true" />
                                                <div>
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getSignificanceColor(item.significance)}`}>
                                                        {item.type}
                                                    </span>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {new Date(item.date).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })}
                                                    </p>
                                                </div>
                                            </div>

                                            <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                                            <p className="text-orange-600 font-semibold text-lg mb-4">{item.organization}</p>
                                            <p className="text-gray-700 text-lg leading-relaxed">{item.description}</p>

                                            {item.link && (
                                                <div className="mt-6">
                                                    <a
                                                        href={item.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors duration-200"
                                                    >
                                                        Learn More
                                                        <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Press Coverage */}
            <section className="py-16 bg-gray-50" aria-labelledby="press-coverage-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:text-center mb-12">
                        <h2 id="press-coverage-heading" className="text-base text-orange-600 font-semibold tracking-wide uppercase">
                            Media Coverage
                        </h2>
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                            Press Articles
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {pressData?.press_coverage?.map((article) => (
                            <article key={article.id} className="bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <Book className="h-5 w-5 text-orange-600" aria-hidden="true" />
                                            <span className="text-sm font-medium text-orange-600">{article.publication}</span>
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                {article.type}
                                            </span>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{article.title}</h3>
                                        <p className="text-gray-600 mb-3">{article.excerpt}</p>
                                        <p className="text-sm text-gray-500">
                                            {new Date(article.date).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                </div>
                                {article.link && (
                                    <a
                                        href={article.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-orange-600 hover:text-orange-500 font-medium inline-flex items-center focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded"
                                    >
                                        Read Article
                                        <ExternalLink className="ml-1 h-4 w-4" aria-hidden="true" />
                                    </a>
                                )}
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Awards & Recognition */}
            {pressData?.awards_recognition && (
                <section className="py-16 bg-white" aria-labelledby="awards-heading">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="lg:text-center mb-12">
                            <h2 id="awards-heading" className="text-base text-orange-600 font-semibold tracking-wide uppercase">
                                Historical Recognition
                            </h2>
                            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                                Awards Timeline
                            </p>
                        </div>

                        <div className="space-y-8">
                            {pressData.awards_recognition.map((period, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <div className="flex items-center justify-center h-10 w-10 rounded-md bg-orange-500 text-white">
                                                <Calendar className="h-6 w-6" aria-hidden="true" />
                                            </div>
                                        </div>
                                        <div className="ml-6 flex-1">
                                            <div className="flex items-center justify-between mb-3">
                                                <h3 className="text-lg font-medium text-gray-900">{period.organization}</h3>
                                                <span className="text-sm text-gray-500 font-medium">{period.year}</span>
                                            </div>
                                            <p className="text-gray-600 mb-4">{period.description}</p>
                                            <div className="space-y-2">
                                                {period.awards?.map((award, awardIndex) => (
                                                    <div key={awardIndex} className="flex items-start">
                                                        <div className="w-2 h-2 bg-yellow-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                                        <span className="text-gray-700">{award}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Academic Contributions */}
            {pressData?.academic_contributions && (
                <section className="py-16 bg-gray-50" aria-labelledby="academic-heading">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="lg:text-center mb-12">
                            <h2 id="academic-heading" className="text-base text-orange-600 font-semibold tracking-wide uppercase">
                                Academic Work
                            </h2>
                            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                                Teaching & Research
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            {pressData.academic_contributions.map((contribution, index) => (
                                <div key={index} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                                    <div className="flex items-start">
                                        <User className="h-6 w-6 text-orange-600 mr-3 mt-1 flex-shrink-0" aria-hidden="true" />
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-lg font-semibold text-gray-900">{contribution.role}</h3>
                                                <span className="text-sm text-gray-500">{contribution.period}</span>
                                            </div>
                                            <p className="text-orange-600 font-medium mb-2">{contribution.institution}</p>
                                            <p className="text-gray-600 font-medium mb-3">{contribution.course}</p>
                                            <p className="text-gray-700">{contribution.description}</p>
                                            {contribution.sponsors && (
                                                <div className="mt-3">
                                                    <span className="text-sm text-gray-500">Sponsored by: </span>
                                                    {contribution.sponsors.map((sponsor, sponsorIndex) => (
                                                        <span key={sponsorIndex} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 ml-1">
                                                            {sponsor}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Media Kit */}
            {pressData?.contact_info && (
                <section className="py-16 bg-orange-50" aria-labelledby="media-kit-heading">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 id="media-kit-heading" className="text-3xl font-extrabold text-gray-900 mb-4">
                            Media Kit & Press Contact
                        </h2>
                        <p className="text-xl text-gray-700 mb-8">
                            {pressData.contact_info.availability}
                        </p>

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Press Contact</h3>
                            <div className="space-y-2">
                                <p className="text-gray-700">
                                    <strong>Name:</strong> {pressData.contact_info.press_contact.name}
                                </p>
                                <p className="text-gray-700">
                                    <strong>Email:</strong>{' '}
                                    <a
                                        href={`mailto:${pressData.contact_info.press_contact.email}`}
                                        className="text-orange-600 hover:text-orange-500"
                                    >
                                        {pressData.contact_info.press_contact.email}
                                    </a>
                                </p>
                                <p className="text-gray-700">
                                    <strong>Phone:</strong>{' '}
                                    <a
                                        href={`tel:${pressData.contact_info.press_contact.phone}`}
                                        className="text-orange-600 hover:text-orange-500"
                                    >
                                        {pressData.contact_info.press_contact.phone}
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </SEOLayout>
    );
};

export default Press;